import React, { useEffect, useMemo, useState } from 'react'
import { Box, FormControlLabel, MenuItem, Radio, TextField, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CustomAlertDialog from '../general/CustomAlertDialog/CustomAlertDialog'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../v2/general/CustomModal/popupButtonStyles'
import { popupInputLabelSx } from '../v2/general/CustomModal/popupInputStyles'
import {
	TimesheetNotificationContentType,
	TimesheetNotificationTemplate,
	useGetMessageTemplatesQuery,
} from '../../store/api/timesheet/timesheetMessageTemplatesApi'

export interface TimesheetNotificationSubmitPayload {
	contentType: TimesheetNotificationContentType
	templateId?: string
	title: string
	message: string
}

interface TimesheetNotificationDialogProps {
	open: boolean
	onClose: () => void
	onConfirm: (payload: TimesheetNotificationSubmitPayload) => Promise<void>
	isSubmitting?: boolean
}

const labels = {
	close: 'Fechar',
	confirmSend: 'Confirmar Envio',
	content: 'Conte\u00fado',
	custom: 'Personalizado',
	loadingTemplates: 'A carregar templates...',
	message: 'Mensagem',
	noTemplates: 'Sem templates dispon\u00edveis',
	sendError: 'N\u00e3o foi poss\u00edvel enviar a notifica\u00e7\u00e3o.',
	template: 'Template de Notifica\u00e7\u00e3o',
	templatesUnavailable: 'Templates indispon\u00edveis',
	title: 'T\u00edtulo',
	useTemplate: 'Usar Template',
	windowTitle: 'Enviar Notifica\u00e7\u00e3o',
}

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const radioSx = {
	color: '#8695AF',
	padding: '9px',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
}

const radioLabelSx = {
	margin: 0,
	flexShrink: 0,
	'.MuiFormControlLabel-label': {
		fontFamily: 'var(--font-family)',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: 'var(--primary-dark)',
	},
}

const notificationFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
		height: '14px',
		lineHeight: '14px',
		overflow: 'visible',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontFamily: 'var(--font-family)',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: 'var(--primary-dark)',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '10px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}

const notificationSelectSx = {
	...notificationFieldSx,
	'& .MuiOutlinedInput-root': {
		...notificationFieldSx['& .MuiOutlinedInput-root'],
		height: '40px',
		minHeight: '40px',
	},
	'& .MuiSelect-select': {
		display: 'flex',
		alignItems: 'center',
		height: '40px',
		minHeight: '0 !important',
		padding: '0 36px 0 12px !important',
		fontFamily: 'var(--font-family)',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: 'var(--primary-dark)',
	},
	'& .MuiSelect-icon': {
		right: '8px',
		color: '#00A5FF',
	},
}

const templateSelectContainerSx = {
	position: 'relative',
	flexShrink: 0,
	width: { xs: '100%', sm: '262px' },
	height: '40px',
	overflow: 'visible',
}

const templateSelectLabelSx = {
	position: 'absolute',
	top: '-6px',
	left: '12px',
	zIndex: 2,
	display: 'flex',
	alignItems: 'center',
	height: '12px',
	padding: '10px 4px',
	backgroundColor: '#FFFFFF',
	fontFamily: 'var(--font-family)',
	fontSize: '11px',
	fontWeight: 400,
	lineHeight: '12px',
	letterSpacing: '0.15px',
	color: 'var(--primary-dark)',
	whiteSpace: 'nowrap',
	pointerEvents: 'none',
}

const readonlyTitleFieldSx = {
	...notificationFieldSx,
	'& .MuiOutlinedInput-root': {
		...notificationFieldSx['& .MuiOutlinedInput-root'],
		backgroundColor: '#F5F7FC',
		'& fieldset': {
			borderColor: 'transparent',
		},
		'&:hover fieldset': {
			borderColor: 'transparent',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'transparent',
		},
	},
}

const getTemplateEmptyLabel = (isLoading: boolean, isError: boolean): string => {
	if (isLoading) {
		return labels.loadingTemplates
	}

	if (isError) {
		return labels.templatesUnavailable
	}

	return labels.noTemplates
}

const TimesheetNotificationDialog = ({
	open,
	onClose,
	onConfirm,
	isSubmitting = false,
}: Readonly<TimesheetNotificationDialogProps>): React.JSX.Element => {
	const [contentType, setContentType] = useState<TimesheetNotificationContentType>('template')
	const [selectedTemplateId, setSelectedTemplateId] = useState('')
	const [title, setTitle] = useState('')
	const [message, setMessage] = useState('')
	const [submitError, setSubmitError] = useState('')

	const {
		data: apiTemplates = [],
		isFetching: isFetchingTemplates,
		isError: isTemplatesError,
	} = useGetMessageTemplatesQuery(undefined, { skip: !open })
	const templates = useMemo<TimesheetNotificationTemplate[]>(() => apiTemplates, [apiTemplates])
	const firstTemplate = templates[0]
	const hasTemplates = templates.length > 0
	const selectedTemplate = useMemo(
		() => templates.find(template => template.id === selectedTemplateId),
		[selectedTemplateId, templates],
	)

	useEffect(() => {
		if (!open) {
			return
		}

		setSubmitError('')

		if (firstTemplate) {
			setContentType('template')
			setSelectedTemplateId(firstTemplate.id)
			setTitle(firstTemplate.title)
			setMessage(firstTemplate.message)
			return
		}

		setContentType('custom')
		setSelectedTemplateId('')
		setTitle('')
		setMessage('')
	}, [firstTemplate, open])

	const handleContentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value

		if (value !== 'custom' && value !== 'template') {
			return
		}

		setContentType(value)
		setSubmitError('')

		if (value === 'template' && !selectedTemplateId && firstTemplate) {
			setSelectedTemplateId(firstTemplate.id)
			setTitle(firstTemplate.title)
			setMessage(firstTemplate.message)
		}
	}

	const handleTemplateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const nextTemplateId = event.target.value
		const nextTemplate = templates.find(template => template.id === nextTemplateId)

		setSelectedTemplateId(nextTemplateId)
		setSubmitError('')

		if (nextTemplate) {
			setTitle(nextTemplate.title)
			setMessage(nextTemplate.message)
		}
	}

	const handleConfirm = async () => {
		setSubmitError('')

		try {
			await onConfirm({
				contentType,
				templateId: contentType === 'template' ? selectedTemplate?.id : undefined,
				title: title.trim(),
				message: message.trim(),
			})
			onClose()
		} catch (_error) {
			setSubmitError(labels.sendError)
		}
	}

	const isConfirmDisabled =
		isSubmitting || !title.trim() || !message.trim() || (contentType === 'template' && !selectedTemplateId)

	return (
		<CustomAlertDialog
			open={open}
			severity='question'
			hideIcon
			showCloseButton={false}
			closeOnBackdropClick={false}
			onRequestClose={onClose}
			maxWidth={false}
			title={
				<Typography
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '16px',
						fontWeight: 600,
						lineHeight: 1.75,
						letterSpacing: '0.15px',
						color: 'var(--primary-dark)',
					}}
				>
					{labels.windowTitle}
				</Typography>
			}
			PaperProps={{
				sx: {
					width: 'calc(100% - 64px)',
					maxWidth: '1152px',
					borderRadius: '4px',
					backgroundColor: '#FFFFFF',
					backgroundImage: 'none',
					boxShadow: dialogShadow,
				},
			}}
			BackdropProps={{
				sx: {
					backgroundColor: 'rgba(9, 32, 86, 0.35)',
					backdropFilter: 'blur(1px)',
				},
			}}
			dialogTitleProps={{
				sx: {
					padding: '16px 24px',
				},
			}}
			dialogContentProps={{
				sx: {
					padding: '24px 24px 16px',
				},
			}}
			dialogActionsProps={{
				sx: {
					padding: '8px',
					gap: '8px',
				},
			}}
			actions={[
				{
					id: 'close-notification-dialog',
					label: labels.close,
					variant: 'text',
					color: 'primary',
					closeOnClick: false,
					onClick: onClose,
					sx: popupSecondaryButtonSx,
				},
				{
					id: 'send-notification',
					label: labels.confirmSend,
					variant: 'contained',
					color: 'primary',
					closeOnClick: false,
					onClick: handleConfirm,
					disabled: isConfirmDisabled,
					isLoading: isSubmitting,
					sx: popupActionButtonSx,
				},
			]}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px',
						flexWrap: 'wrap',
					}}
				>
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '14px',
							fontWeight: 600,
							lineHeight: 1.57,
							color: 'var(--primary-dark)',
						}}
					>
						{labels.content}
					</Typography>

					<FormControlLabel
						value='custom'
						control={
							<Radio
								size='small'
								checked={contentType === 'custom'}
								onChange={handleContentTypeChange}
								sx={radioSx}
							/>
						}
						label={labels.custom}
						sx={radioLabelSx}
					/>
					<FormControlLabel
						value='template'
						control={
							<Radio
								size='small'
								checked={contentType === 'template'}
								onChange={handleContentTypeChange}
								sx={radioSx}
							/>
						}
						label={labels.useTemplate}
						disabled={!hasTemplates && !isFetchingTemplates}
						sx={radioLabelSx}
					/>

					<Box sx={templateSelectContainerSx}>
						<Typography
							component='span'
							sx={templateSelectLabelSx}
						>
							{labels.template}
						</Typography>
						<TextField
							select
							size='small'
							value={selectedTemplateId}
							onChange={handleTemplateChange}
							disabled={contentType !== 'template' || !hasTemplates}
							SelectProps={{
								IconComponent: KeyboardArrowDownIcon,
								inputProps: { 'aria-label': labels.template },
							}}
							sx={notificationSelectSx}
							fullWidth
						>
							{!hasTemplates && (
								<MenuItem
									value=''
									disabled
								>
									{getTemplateEmptyLabel(isFetchingTemplates, isTemplatesError)}
								</MenuItem>
							)}
							{templates.map(template => (
								<MenuItem
									key={template.id}
									value={template.id}
								>
									{template.title}
								</MenuItem>
							))}
						</TextField>
					</Box>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
					<TextField
						label={labels.title}
						size='small'
						value={title}
						onChange={event => setTitle(event.target.value)}
						InputLabelProps={{ shrink: true }}
						InputProps={{ readOnly: contentType === 'template' }}
						sx={contentType === 'template' ? readonlyTitleFieldSx : notificationFieldSx}
						fullWidth
					/>
					<TextField
						label={labels.message}
						size='small'
						value={message}
						onChange={event => setMessage(event.target.value)}
						InputLabelProps={{ shrink: true }}
						InputProps={{ readOnly: contentType === 'template' }}
						sx={contentType === 'template' ? readonlyTitleFieldSx : notificationFieldSx}
						fullWidth
						multiline
						minRows={2}
					/>
				</Box>

				{submitError && (
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '12px',
							fontWeight: 500,
							color: '#D32F2F',
						}}
					>
						{submitError}
					</Typography>
				)}
			</Box>
		</CustomAlertDialog>
	)
}

export default TimesheetNotificationDialog
